import { useCallback, useEffect, useState } from 'react';

import { localStorage } from '@change-corgi/core/storage';

import { useCookiePrefAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

const DISMISSED_STORAGE_KEY = 'spiking_petition_banner_dismissed_petition_id';

type Result = Readonly<{
	// False until cookie prefs and (with consent) the stored value have loaded. Callers must not
	// render or track anything before this is true, or a dismissed user would flash and fire exposure.
	ready: boolean;
	dismissed: boolean;
	dismiss: () => void;
}>;

/**
 * Remembers the id of the last spiking petition the user dismissed (closed the banner or clicked it).
 * The banner stays hidden only while the spiking petition is that same one, so a different spiking
 * petition shows again.
 *
 * With `preferences` consent the id lives in localStorage. Without consent (or before it loads)
 * localStorage is neither read nor written, and the dismissal is kept in memory. If consent is granted
 * later in the same page load, the in-memory dismissal is saved to localStorage.
 */
export function useBannerDismissal(spikingPetitionId: string): Result {
	const consentState = useCookiePrefAsync('preferences');
	const hasConsent = isLoaded(consentState) ? consentState.value : false;

	// Undefined until read. The shared storage hooks read on mount whatever the consent, so they can't be used here.
	const [storedPetitionId, setStoredPetitionId] = useState<string | null | undefined>(undefined);
	const [inMemoryPetitionId, setInMemoryPetitionId] = useState<string | null>(null);

	useEffect(() => {
		if (!hasConsent) return;
		// A dismissal made before consent was granted is the latest one, so it replaces the stored value.
		if (inMemoryPetitionId !== null) {
			localStorage.setItem(DISMISSED_STORAGE_KEY, inMemoryPetitionId);
			setStoredPetitionId(inMemoryPetitionId);
			return;
		}
		setStoredPetitionId(localStorage.getItem(DISMISSED_STORAGE_KEY));
	}, [hasConsent, inMemoryPetitionId]);

	const dismiss = useCallback(() => {
		if (hasConsent) {
			localStorage.setItem(DISMISSED_STORAGE_KEY, spikingPetitionId);
			setStoredPetitionId(spikingPetitionId);
		} else {
			setInMemoryPetitionId(spikingPetitionId);
		}
	}, [hasConsent, spikingPetitionId]);

	const settled = isLoaded(consentState) && (!hasConsent || storedPetitionId !== undefined);
	// Once ready, stay ready. Consent granted later in the page load would otherwise drop `ready` for the
	// render before localStorage is read, unmounting the banner and re-firing its view and `$exposure`.
	// Derived state, so it's set during render: an effect would leave that one render not-ready.
	const [wasReady, setWasReady] = useState(false);
	if (settled && !wasReady) setWasReady(true);

	// Until localStorage has been read, the in-memory dismissal is the current one.
	const dismissedPetitionId = hasConsent && storedPetitionId !== undefined ? storedPetitionId : inMemoryPetitionId;

	return {
		ready: settled || wasReady,
		dismissed: dismissedPetitionId === spikingPetitionId,
		dismiss,
	};
}
